import {useNavigate} from "react-router-dom";
import React, {useContext, useEffect, useState} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  Stack,
  Typography
} from "@mui/material";
import {
  CONTEXT_ACTION_TYPE,
  DataStoreContext,
  HandSelectionStepPayload,
  StepPayload,
  STEPS,
} from "../../contexts/HandSelectionContext";
import useNotification from "../../../../utils/notificationUtil";
import {findAttachment, findFingertip, findHand} from "../../../../utils/awsAmplifyUtil";
import {HAND_SELECTION_URLS} from "../../../../constants/constants";
import SearchCriteriaTable from "../tables/SearchCriteriaTable";
import {handleInputChangeSelect} from "../../../../utils/formUtil";
import {
  CALCULATED_SORT_ORDER,
  CALCULATED_SORT_ORDER_LABEL,
  CalculatedSortOrder
} from "../../../../constants/handselection";
import {hasFingertipIdResult, isConnectable, sortCalculationResults,} from "../../../../utils/handselectionUtil";
import {ArmDocument} from "../../../equipmentdb/types/pj1dbApiArm";
import useFetchAllQuery, {Status, STATUSES, useFetchAllQueryHelper} from "../../../../hooks/useFetchAllQuery";
import {AttachmentDocument} from "../../../equipmentdb/types/pj1dbApiAttachment";
import HandGroupListItem, {HandGroupListItemProps} from "../lists/HandGroupListItem";
import {Result} from "../../../../types/pj1dbApiHandling";

export interface HandSelectionStepProps {
  isError: boolean;
  isLoading: boolean;
  sortOrder: CalculatedSortOrder;
  changeSortOrder: SelectProps<CalculatedSortOrder>['onChange'];
  onSelectHand: HandGroupListItemProps['onSelectHand'];
}


export default function HandSelectionStep(props: HandSelectionStepProps) {
  const {state, dispatch} = useContext(DataStoreContext);
  const navigate = useNavigate();

  return (
    <Container
      sx={{px: 0, py: 2, display: "flex", flexDirection: "row", flexWrap: "wrap"}}
      maxWidth={false}
    >
      <Stack width='100%' gap={4}>
        <Box width='100%'>
          <Box display='flex' justifyContent='space-between'>
            <Typography variant='h6' component='h2'>計算条件</Typography>
            <Button
              sx={{py: 0}}
              onClick={() => {
                dispatch({
                  type: CONTEXT_ACTION_TYPE.STEP,
                  payload: STEPS.SEARCH_CRITERIA_ADDITIONAL satisfies StepPayload,
                })
                navigate(HAND_SELECTION_URLS.BEGINNING_WORK.URL);
              }}
              variant='text'
              color='inherit'
            >計算条件の変更</Button>
          </Box>

          <SearchCriteriaTable
            criteria={state.searchCriteriaAdditionalStep.searchCriteriaList}
            handPowerSources={state.searchCriteriaAdditionalStep.handPowerSourceInputs.handPowerSource}
            selectedArm={state.searchCriteriaAdditionalStep.selectedArm}
          />
        </Box>

        <Stack width='100%' gap={2}>
          <Box display='flex' justifyContent='space-between'>
            <Typography variant='h6' component='h2'>計算結果一覧</Typography>
            <FormControl sx={{minWidth: 120}} size="small">
              <InputLabel id="sort-order-label">並び順</InputLabel>
              <Select
                labelId="sort-order-label"
                name="sortOrder"
                label="並び順"
                value={props.sortOrder}
                onChange={props.changeSortOrder}
                sx={{bgcolor: "white"}}
              >
                {Object.values(CALCULATED_SORT_ORDER).map((value) => (
                  <MenuItem key={value} value={value}>{CALCULATED_SORT_ORDER_LABEL[value]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* タブ切り替えで再ロードされるので、ロード中でもすでにデータがあればローディングにしない */}
          {props.isLoading && state.handSelectionStep.hands.length <= 0 && (
            <Box display='flex' mt={2}>
              <CircularProgress/>
            </Box>
          )}

          {/* タブ切り替えで再ロードされるので、ロード中でもすでにデータがあれば表示するようにしている */}
          {state.handSelectionStep.hands.length > 0 && (
            <HandList
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
              results={sortCalculationResults(state.calculatedHand?.results ?? [], props.sortOrder)}
              selectedArm={state.searchCriteriaAdditionalStep.selectedArm}
              attachments={state.handSelectionStep.attachments}
              status={state.handSelectionStep.attachmentStatus}
              hands={state.handSelectionStep.hands}
              fingertips={state.handSelectionStep.fingertips}
            />)}

          {state.handSelectionStep.hands.length === 0 && !props.isLoading && !props.isError && (
            <Box width='100%' textAlign='center'>
              <Typography variant="subtitle1" sx={{fontWeight: "bold"}}
                          dangerouslySetInnerHTML={{__html: '該当データがありません。'}}/>
            </Box>
          )}
          {props.isError && (
            <Box width='100%' textAlign='center'>
              <Typography
                variant="subtitle1"
                sx={{fontWeight: "bold"}}
                dangerouslySetInnerHTML={{__html: '検索処理が失敗しました。'}}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    </Container>
  );
}

function HandList({selectedArm, ...props}: HandListProps & {
  selectedArm: ArmDocument | null,
  attachments: AttachmentDocument[],
  status: Status
}) {
  if (selectedArm) {
    const connectable = props
      .results
      .filter((r) => r.hands.every(h => isConnectable(h.handID, selectedArm?.attr.armId ?? '', props.attachments)));
    const unconnected = props
      .results
      .filter((r) => r.hands.some(h => !isConnectable(h.handID, selectedArm?.attr.armId ?? '', props.attachments)));

    if (props.status === STATUSES.FETCHING) {
      return (
        <Box display='flex' justifyContent='center'>
          <CircularProgress/>
        </Box>
      )
    }

    return (
      <Stack gap={4}>
        <Stack gap={1}>
          <Typography variant='subtitle1' fontWeight='bold' component='h3'>接続可能ハンド一覧</Typography>
          {
            connectable.length === 0
              ? <Typography variant='body1' component='h3'>該当データがありません。</Typography>
              // eslint-disable-next-line react/jsx-props-no-spreading
              : <BaseHandList {...props} results={connectable}/>
          }
        </Stack>
        <Stack gap={1}>
          <Typography variant='subtitle1' fontWeight='bold' component='h3'>接続不可能ハンド一覧</Typography>
          {
            unconnected.length === 0
              ? <Typography variant='body1' component='h3'>該当データがありません。</Typography>
              // eslint-disable-next-line react/jsx-props-no-spreading
              : <BaseHandList {...props} results={unconnected}/>
          }
        </Stack>
      </Stack>
    )
  }

  /* eslint-disable-next-line react/jsx-props-no-spreading */
  return (<BaseHandList {...props} />)
}

interface HandListProps {
  results: Result[];
  hands: HandSelectionStepPayload['hands'];
  fingertips: HandSelectionStepPayload['fingertips']
  onSelectHand: HandGroupListItemProps['onSelectHand'];
}

function BaseHandList(props: HandListProps) {
  return (
    <Stack gap={2}>
      {props.results.map((group) => (
        <HandGroupListItem
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          key={JSON.stringify(group)}
          group={group}
          hands={props.hands.flatMap((h) => Object.values(h))}
          fingertips={props.fingertips.flatMap((f) => Object.values(f))}
          onSelectHand={props.onSelectHand}
        />
      ))}
    </Stack>
  )
}

export function useHandSelectionStepHelpers() {
  const {state, dispatch} = useContext(DataStoreContext)
  const {errorMsg} = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [sortOrder, setSortOrder] = useState<CalculatedSortOrder>(state.handSelectionStep.sortOrder);
  const handIds = state.calculatedHand?.results.flatMap(r => r.hands.map(h => h.handID)) ?? [];
  const attachmentResult = useAttachments(handIds, state.searchCriteriaAdditionalStep.selectedArm);
  useFetchAllQueryHelper({
    onChange: (dispatchData, newStatus) => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.HAND_SELECTION_STEP, payload: {
          ...state.handSelectionStep,
          attachments: dispatchData
            .filter((item): item is Exclude<typeof item, undefined> => item !== undefined)
            .map(item => Object.values(item))
            .flat(),
          attachmentStatus: newStatus,
        } satisfies HandSelectionStepPayload
      });
    },
    data: attachmentResult.data,
    status: attachmentResult.status,
  });

  const changeSortOrder: HandSelectionStepProps['changeSortOrder'] = (event) => {
    handleInputChangeSelect<CalculatedSortOrder>(event, "sortOrder", sortOrder, setSortOrder);
    const newSortOrder = event.target.value as CalculatedSortOrder;
    setSortOrder(newSortOrder);
    dispatch({
      type: CONTEXT_ACTION_TYPE.HAND_SELECTION_STEP,
      payload: {
        ...state.handSelectionStep,
        sortOrder: newSortOrder,
      } satisfies HandSelectionStepPayload
    });
  }

  const selectHand: HandGroupListItemProps['onSelectHand'] = (data) => {
    dispatch({
      type: CONTEXT_ACTION_TYPE.HAND_SELECTION_STEP,
      payload: {
        ...state.handSelectionStep,
        selectedHandResult: data,
        step: STEPS.HARDWARE_SELECTION,
      } satisfies HandSelectionStepPayload
    });
  }

  const findHandProc = async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const [handResult, fingertipResult] = await Promise.all([
        findHand({
          "query": {
            "handId": {"$in": state.calculatedHand?.results.flatMap((r) => r.hands.map(h => h.handID)) ?? []}
          }
        }),
        findFingertip({
          "query": {
            "fingertipId": {
              "$in": state
                .calculatedHand
                ?.results
                .flatMap((r) => r.hands.filter(hasFingertipIdResult).map(h => h.fingertipID)) ?? []
            }
          }
        })
      ]);
      if (!handResult.isSuccess || !fingertipResult.isSuccess || !handResult.data || !fingertipResult.data) {
        errorMsg("ハンド・爪情報の取得に失敗しました。");
        setIsLoading(false);
        setIsError(true);
        return;
      }

      dispatch({
        type: CONTEXT_ACTION_TYPE.HAND_SELECTION_STEP,
        payload: {
          ...state.handSelectionStep,
          hands: [handResult.data],
          fingertips: [fingertipResult.data],
        } satisfies HandSelectionStepPayload
      });

      attachmentResult.fetchAll().catch(console.error);
    } catch (e) {
      errorMsg("ハンド・爪情報の取得に失敗しました。");
      setIsError(true);
      console.log(e);
    }

    setIsLoading(false);
  }

  return {
    isLoading,
    isError,
    sortOrder,
    setIsLoading,
    changeSortOrder,
    selectHand,
    findHandProc,
  }
}

function useAttachments(handIds: string[], arm: ArmDocument | null) {
  return useFetchAllQuery<Awaited<ReturnType<(typeof findAttachment)>>['data']>({
    queryFn: (query) => findAttachment({
      query: {
        handIds: {
          $regex: Array.from(new Set(handIds)).join("|"),
        },
        armIds: {
          $regex: arm?.attr.armId ?? "",
        }
      }
    }, query),
    needToFetchMore: (data, limit) => Object.entries(data?.items ?? {}).length === limit,
  });
}
